/**
 * printDateTime.ts — company-timezone date/time formatting for PRINTED documents
 * (receipt slip, classic invoice dialogs, visual-builder renderer). Display only:
 * nothing here changes what is stored or sent to the server.
 *
 * Mirrors the desktop app's src/utils/printDateTime.ts. Two problems solved:
 *
 *  1. `SaleOrder.CreatedDate` is written server-side as `DateTime.UtcNow`, but it
 *     comes back out of SQL Server `datetime` columns with `DateTimeKind.Unspecified`,
 *     so the JSON carries NO trailing `Z` ("2026-08-20T09:34:12.5"). ECMAScript reads
 *     a zone-less date-TIME string as *browser-local*, so the printed receipt showed
 *     the raw UTC clock reading. `parsePrintDate` (via the existing `parseServerDate`)
 *     treats exactly those strings as UTC and never double-shifts an already-tagged one.
 *
 *  2. The rendered time must read in the COMPANY's configured time zone rather than
 *     the browser's. The zone comes from the synced company settings
 *     (sessionStorage `companySettings`: `timeZoneId` / `timeZoneName`), falling back
 *     to the login JWT's `timeZone` claim (localStorage `employeeProfile`, shaped like
 *     "Pakistan Standard Time (Asia/Karachi)" — the IANA id is inside the parentheses).
 *     Both sources may carry a WINDOWS zone id ("Pakistan Standard Time" — the settings
 *     picker is backed by TimeZoneInfo.GetSystemTimeZones on a Windows API host), which
 *     `Intl` rejects, so resolution tries candidates until one is browser-usable:
 *     raw value → parenthesised IANA extract → WINDOWS_TO_IANA map.
 *
 * Fallbacks are deliberately total: an unknown/blank/invalid zone formats in the
 * browser's own zone exactly as before (a `RangeError` from `Intl` is caught, never
 * thrown), and an unparseable value is echoed back verbatim — never "Invalid Date".
 */

import { parseServerDate } from './serverTime';

/**
 * Windows time-zone id → IANA id, for the zones this product's market actually
 * meets. `CompanySettings.TimeZoneId` is filled from a picker backed by
 * `TimeZoneInfo.GetSystemTimeZones()` on the API host — a WINDOWS host serves
 * Windows ids ("Pakistan Standard Time"), which every browser's `Intl` rejects.
 * The JWT `timeZone` claim's prefix is the same Windows id. Compact by design:
 * an unmapped Windows id simply falls through to the next candidate in the
 * resolution chain (worst case: browser zone, the pre-fix behaviour).
 */
const WINDOWS_TO_IANA: Record<string, string> = {
  'Pakistan Standard Time': 'Asia/Karachi',
  'Arabian Standard Time': 'Asia/Dubai', // (UTC+04:00) Abu Dhabi, Muscat
  'Arab Standard Time': 'Asia/Riyadh', // (UTC+03:00) Kuwait, Riyadh
  'Arabic Standard Time': 'Asia/Baghdad',
  'Gulf Standard Time': 'Asia/Dubai', // defensive alias — some sources use it for the Gulf
  'Oman Standard Time': 'Asia/Muscat', // defensive alias — Windows folds Oman into Arabian
  'India Standard Time': 'Asia/Kolkata',
  'Bangladesh Standard Time': 'Asia/Dhaka',
  'Sri Lanka Standard Time': 'Asia/Colombo',
  'Nepal Standard Time': 'Asia/Kathmandu',
  'Afghanistan Standard Time': 'Asia/Kabul',
  'Iran Standard Time': 'Asia/Tehran',
  'Turkey Standard Time': 'Europe/Istanbul',
  'GMT Standard Time': 'Europe/London',
  'Egypt Standard Time': 'Africa/Cairo',
  'W. Europe Standard Time': 'Europe/Berlin',
  'Eastern Standard Time': 'America/New_York',
  'Pacific Standard Time': 'America/Los_Angeles',
  'China Standard Time': 'Asia/Shanghai',
  'Singapore Standard Time': 'Asia/Singapore',
  'Central Asia Standard Time': 'Asia/Almaty',
};

/** True when `Intl` accepts the zone id (it throws a RangeError for unknown ones). */
function isUsableTimeZone(candidate: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: candidate });
    return true;
  } catch {
    return false;
  }
}

/**
 * Pull a usable zone id out of a raw setting by trying candidates until `Intl`
 * accepts one:
 *   1. the raw value itself — a plain IANA id ("Asia/Karachi") or "UTC";
 *   2. the parenthesised part — the JWT claim's Windows-plus-IANA form
 *      ("Pakistan Standard Time (Asia/Karachi)");
 *   3. the WINDOWS_TO_IANA map, keyed by the part before any parenthesis and by
 *      the whole value — covers a bare Windows id ("Pakistan Standard Time",
 *      served by GET api/CompanySettings/timezones on a Windows host).
 * Returns null when no candidate is a zone the browser accepts (e.g. a display
 * name like "(UTC+05:00) Islamabad, Karachi" on browsers without offset-zone
 * support).
 */
export function resolveIanaTimeZone(raw: string | null | undefined): string | null {
  const value = (raw ?? '').trim();
  if (!value) return null;
  const parenthesised = value.match(/\(([^)]+)\)/)?.[1]?.trim();
  const beforeParenthesis = value.split('(')[0]?.trim();
  const candidates = [
    value,
    parenthesised,
    beforeParenthesis ? WINDOWS_TO_IANA[beforeParenthesis] : undefined,
    WINDOWS_TO_IANA[value],
  ];
  for (const candidate of candidates) {
    if (candidate && isUsableTimeZone(candidate)) return candidate;
  }
  return null;
}

/**
 * True for the PLACEHOLDER zone the Settings screen fabricates when a company has
 * no zone chosen: TimeZoneId "UTC" with TimeZoneName "UTC" (or blank).
 *
 * Same rule as the API (Common.Helper.CompanyTimeZoneRules) and the desktop
 * (isPlaceholderUtcZone), so all three agree on whether a zone was chosen.
 *
 * This used to also require the row to have NO companySettingsIdPK — but saving the
 * Settings screen (or the mobile app, or a profile country change) PERSISTS the
 * fabricated pair, after which the row has a PK and the placeholder slipped through:
 * Intl accepts "UTC", so every receipt was pinned to the UTC clock. A deliberately
 * picked UTC is unaffected — the picker saves it with its display name,
 * "(UTC) Coordinated Universal Time", never the bare "UTC".
 */
export function isPlaceholderUtcZone(
  timeZoneId: string | null | undefined,
  timeZoneName: string | null | undefined
): boolean {
  const id = (timeZoneId ?? '').trim().toUpperCase();
  const name = (timeZoneName ?? '').trim().toUpperCase();
  return id === 'UTC' && (name === 'UTC' || name === '');
}

/** Company zone from the synced company settings (sessionStorage `companySettings`). */
function timeZoneFromCompanySettings(): string | null {
  try {
    const settings = JSON.parse(sessionStorage.getItem('companySettings') || '{}') as Record<string, unknown>;
    const timeZoneId = typeof settings.timeZoneId === 'string' ? settings.timeZoneId : null;
    const timeZoneName = typeof settings.timeZoneName === 'string' ? settings.timeZoneName : null;
    // The placeholder is "no zone chosen", not a choice — let the JWT `timeZone`
    // claim (the Business Profile zone) decide instead.
    if (isPlaceholderUtcZone(timeZoneId, timeZoneName)) return null;
    return resolveIanaTimeZone(timeZoneId) ?? resolveIanaTimeZone(timeZoneName);
  } catch {
    return null;
  }
}

/** The `timeZone` claim the login JWT carries, cached into localStorage `employeeProfile`. */
function timeZoneFromEmployeeProfile(): string | null {
  try {
    const profile = JSON.parse(localStorage.getItem('employeeProfile') || '{}') as Record<string, unknown>;
    return resolveIanaTimeZone(typeof profile.timeZone === 'string' ? profile.timeZone : null);
  } catch {
    return null;
  }
}

/**
 * The company IANA zone for the print renderers, or null when none is usable
 * (null → the formatters below fall back to the browser's own zone).
 * Company settings are authoritative; the JWT claim stands in when they carry
 * no browser-usable zone. Read per call — both stores are synchronous and the
 * value must follow a settings re-sync without a reload.
 */
export function getPrintTimeZone(): string | null {
  return timeZoneFromCompanySettings() ?? timeZoneFromEmployeeProfile();
}

/**
 * Parse a printable timestamp. Strings go through `parseServerDate`, which treats
 * zone-less server stamps as UTC and never double-shifts an already-tagged value.
 * Null when missing or unparseable — never an `Invalid Date`.
 */
export function parsePrintDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const fromNumber = new Date(value);
    return isNaN(fromNumber.getTime()) ? null : fromNumber;
  }
  return parseServerDate(value);
}

/**
 * A bare calendar date ("2026-08-17") — a DAY, not an instant. `new Date()` reads
 * one as UTC midnight, so rendering it in a zone west of UTC would print the day
 * before. Those values are formatted in UTC instead, which gives back exactly the
 * day that was written, in every zone.
 */
const CALENDAR_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Zone to render `value` in: UTC for a bare calendar date, otherwise the company zone. */
function zoneForValue(
  value: string | number | Date | null | undefined,
  timeZone: string | null | undefined,
): string | null {
  if (typeof value === 'string' && CALENDAR_DATE_RE.test(value.trim())) return 'UTC';
  return timeZone === undefined ? getPrintTimeZone() : timeZone;
}

/**
 * Format a timestamp for print in the company's time zone via `toLocaleString`.
 * `locales`/`options` pass straight through, so every template keeps its exact
 * number/format style — only the zone (and therefore the instant shown) changes.
 * No/unusable zone → the browser's zone, exactly as before; an unparseable
 * string is echoed back verbatim rather than rendering "Invalid Date".
 */
export function formatPrintDateTime(
  value: string | number | Date | null | undefined,
  locales?: string | string[],
  options?: Intl.DateTimeFormatOptions,
  timeZone?: string | null,
): string {
  const date = parsePrintDate(value);
  if (!date) return typeof value === 'string' ? value : '';
  const zone = zoneForValue(value, timeZone);
  try {
    return date.toLocaleString(locales, zone ? { ...options, timeZone: zone } : options);
  } catch {
    try {
      return date.toLocaleString(locales, options);
    } catch {
      return date.toLocaleString();
    }
  }
}

/** `formatPrintDateTime` for the DATE half (`toLocaleDateString`). */
export function formatPrintDate(
  value: string | number | Date | null | undefined,
  locales?: string | string[],
  options?: Intl.DateTimeFormatOptions,
  timeZone?: string | null,
): string {
  const date = parsePrintDate(value);
  if (!date) return typeof value === 'string' ? value : '';
  const zone = zoneForValue(value, timeZone);
  try {
    return date.toLocaleDateString(locales, zone ? { ...options, timeZone: zone } : options);
  } catch {
    try {
      return date.toLocaleDateString(locales, options);
    } catch {
      return date.toLocaleDateString();
    }
  }
}

/** `formatPrintDateTime` for the TIME half (`toLocaleTimeString`). */
export function formatPrintTime(
  value: string | number | Date | null | undefined,
  locales?: string | string[],
  options?: Intl.DateTimeFormatOptions,
  timeZone?: string | null,
): string {
  const date = parsePrintDate(value);
  if (!date) return typeof value === 'string' ? value : '';
  const zone = zoneForValue(value, timeZone);
  try {
    return date.toLocaleTimeString(locales, zone ? { ...options, timeZone: zone } : options);
  } catch {
    try {
      return date.toLocaleTimeString(locales, options);
    } catch {
      return date.toLocaleTimeString();
    }
  }
}
